import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Settings.css';

const Settings = ({ user: initialUser, onLogout, onUserUpdate }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    phone: '',
    department: '',

    company: {
      company_name: '',
      company_email: '',
      company_phone: '',
      company_address: '',
      contact_person: '',
      contact_person_phone: ''
    }
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/auth.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ action: 'get_profile' })
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Failed to load profile');
        }

        const { user, company } = result;

        setFormData({
          full_name: user.full_name || '',
          username: user.username || '',
          email: user.email || '',
          phone: user.phone || '',
          department: user.department || '',

          company: {
            company_name: company?.company_name || '',
            company_email: company?.company_email || '',
            company_phone: company?.company_phone || '',
            company_address: company?.company_address || '',
            contact_person: company?.contact_person || user.full_name || '',
            contact_person_phone: company?.contact_person_phone || user.phone || ''
          }
        });

        onUserUpdate?.({ ...user, ...company });

      } catch (err) {
        setError(err.message || 'Could not load your profile');
        console.error('Profile fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [onUserUpdate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('company.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        company: { ...prev.company, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setError(''); // clear error when user types
  };

  // Pure function — no side effects (used for button disabled)
  const isPasswordFormValid = () => {
    const { current_password, new_password, confirm_password } = passwordData;

    if (!current_password.trim()) return false;
    if (!new_password.trim()) return false;
    if (!confirm_password.trim()) return false;
    if (new_password.length < 8) return false;
    if (!/[a-z]/.test(new_password)) return false;
    if (!/[A-Z]/.test(new_password)) return false;
    if (!/\d/.test(new_password)) return false;
    if (new_password !== confirm_password) return false;
    if (new_password === current_password) return false;

    return true;
  };

  // Called only on submit — shows specific errors
  const validatePasswordForm = () => {
    const { current_password, new_password, confirm_password } = passwordData;

    if (!current_password.trim()) {
      setError('Current password is required');
      return false;
    }
    if (!new_password.trim()) {
      setError('New password is required');
      return false;
    }
    if (new_password.length < 8) {
      setError('New password must be at least 8 characters long');
      return false;
    }
    if (!/[a-z]/.test(new_password)) {
      setError('New password must contain at least one lowercase letter');
      return false;
    }
    if (!/[A-Z]/.test(new_password)) {
      setError('New password must contain at least one uppercase letter');
      return false;
    }
    if (!/\d/.test(new_password)) {
      setError('New password must contain at least one number');
      return false;
    }
    if (new_password !== confirm_password) {
      setError('New passwords do not match');
      return false;
    }
    if (new_password === current_password) {
      setError('New password must be different from current password');
      return false;
    }

    setError('');
    return true;
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      const payload = {
        action: 'update_profile',
        ...formData,
      };

      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/auth.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Update failed');
      }

      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(''), 4000);

    } catch (err) {
      setError(err.message || 'Failed to save profile changes');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) return;

    setPasswordLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      const payload = {
        action: 'change_password',
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        confirm_password: passwordData.confirm_password
      };

      const response = await fetch(process.env.REACT_APP_API_BASE_URL+'/auth.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Password change failed');
      }

      setSuccess('Password changed successfully! Logging you out in 3 seconds...');

      setTimeout(() => {
        localStorage.removeItem('token');
        onLogout?.();
        navigate('/login');
      }, 3000);

      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });

    } catch (err) {
      setError(err.message || 'Failed to change password');
      console.error(err);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Discard all unsaved changes?')) {
      window.location.reload();
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-card">
          <div className="loading">Loading your settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-card">
        <div className="settings-header">
          <div className="logo-container">
            <div className="logo-icon">🚛</div>
            <h1>Settings</h1>
          </div>
          <p className="settings-subtitle">Update your profile & company information</p>
        </div>

        <form onSubmit={handleSaveProfile} className="settings-form">

          {error && (
            <div className="alert alert-danger">
              <span>⚠️</span> {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span>✅</span> {success}
            </div>
          )}

          {/* ──────────────────────────────────────────────
              COMPANY INFORMATION
          ────────────────────────────────────────────── */}
          <div className="form-section">
            <h3>Company Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="company.company_name"
                  value={formData.company.company_name}
                  onChange={handleInputChange}
                  placeholder="Company name"
                />
              </div>

              <div className="form-group">
                <label>Company Email *</label>
                <input
                  type="email"
                  name="company.company_email"
                  value={formData.company.company_email}
                  onChange={handleInputChange}
                  placeholder="company@example.com"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Company Phone</label>
                <input
                  type="tel"
                  name="company.company_phone"
                  value={formData.company.company_phone}
                  onChange={handleInputChange}
                  placeholder="+91 98765 43210"
                />
              </div>

              <div className="form-group">
                <label>Contact Person</label>
                <input
                  type="text"
                  name="company.contact_person"
                  value={formData.company.contact_person}
                  onChange={handleInputChange}
                  placeholder="Primary contact name"
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>Company Address</label>
              <textarea
                name="company.company_address"
                value={formData.company.company_address}
                onChange={handleInputChange}
                placeholder="Street, City, State, PIN, Country"
                rows={3}
              />
            </div>
          </div>

          {/* ──────────────────────────────────────────────
              PERSONAL INFORMATION
          ────────────────────────────────────────────── */}
          <div className="form-section">
            <h3>Personal Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  disabled
                  className="disabled-input"
                />
                <small>Username cannot be changed</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@company.com"
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+91 ..."
                />
              </div>
            </div>

            <div className="form-group">
              <label>Department / Team</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                placeholder="Operations, Sales, IT, Management..."
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || loading}
            >
              {saving ? (
                <>
                  <span className="spinner"></span> Saving...
                </>
              ) : (
                'Save Profile Changes'
              )}
            </button>

            <button
              type="button"
              className="btn-secondary"
              onClick={handleReset}
              disabled={saving || loading}
            >
              Discard Changes
            </button>
          </div>
        </form>

        {/* ──────────────────────────────────────────────
            PASSWORD CHANGE SECTION
        ────────────────────────────────────────────── */}
        <div className="form-section">
          <div className="section-header">
            <h3>Change Password</h3>
            <button
              type="button"
              className="btn-link"
              onClick={() => setShowPasswordSection(!showPasswordSection)}
            >
              {showPasswordSection ? 'Hide' : 'Change Password'}
            </button>
          </div>

          {showPasswordSection && (
            <form onSubmit={handlePasswordSave} className="password-form">

              <div className="form-row">
                <div className="form-group">
                  <label>Current Password *</label>
                  <input
                    type="password"
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    placeholder="Enter current password"
                  />
                </div>

                <div className="form-group">
                  <label>New Password *</label>
                  <input
                    type="password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    placeholder="Enter new password"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Confirm New Password *</label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              <div className="form-actions password-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={passwordLoading || !isPasswordFormValid()}
                >
                  {passwordLoading ? (
                    <>
                      <span className="spinner"></span> Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </button>

                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setPasswordData({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                    setError('');
                  }}
                  disabled={passwordLoading}
                >
                  Clear Form
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="settings-footer">
          <p>To change your role or for other account issues, please contact your system administrator.</p>
        </div>
      </div>
    </div>
  );
};

export default Settings;